// The Claude connector, owner's side: the approval screen claude.ai sends the owner to (#/connect),
// and the home-page card with the connector address and connected apps.
import { api } from "./api";
import { date, esc } from "./format";

export const connectorUrl = () => `${location.origin}/mcp`;

// A new claude.ai chat that assesses one stock through the connector (see get_assessment_guide).
export const askClaudeUrl = (code: string) =>
  `https://claude.ai/new?q=${encodeURIComponent(
    `Using the Gold connector, assess ${code}. Call start_assessment for ${code} (it has the guide and everything Gold holds, including previous assessments) and follow the guide: research with web search (news, ASX announcements and reports, HotCopper, Reddit and other forums), then save the assessment to Gold with save_assessment.`,
  )}`;

export async function renderConnect(app: HTMLElement, query: string) {
  const params = Object.fromEntries(new URLSearchParams(query));
  app.innerHTML = `<section class="card connect"><div class="loading" role="status"><span></span>Checking the request…</div></section>`;
  const box = app.querySelector(".connect")!;
  let name: string;
  try {
    name = (await api<{ name: string }>(`oauth/client?${new URLSearchParams(params)}`)).name;
  } catch (e) {
    box.innerHTML = `<h2>Can't connect</h2><div class="notice error">${esc((e as Error).message)}</div><a href="#/">Back to Gold</a>`;
    return;
  }
  box.innerHTML = `
    <h2>Connect ${esc(name)} to Gold?</h2>
    <p>${esc(name)} will be able to, on your behalf:</p>
    <ul>
      <li>read stock data, your watchlist, notes, portfolio and research</li>
      <li>record trades, save research and notes, and change your watchlist</li>
    </ul>
    <p class="muted small">You can disconnect it at any time from the Gold home page.</p>
    <div class="error" id="connect-error"></div>
    <div class="actions"><button class="ghost" id="deny">Cancel</button><button class="primary" id="allow">Allow</button></div>`;
  const go = async (path: "approve" | "deny") => {
    box.querySelectorAll("button").forEach((b) => (b.disabled = true));
    try {
      const r = await api<{ redirect: string }>(`oauth/${path}`, "POST", params);
      location.href = r.redirect;
    } catch (e) {
      document.getElementById("connect-error")!.textContent = (e as Error).message;
      box.querySelectorAll("button").forEach((b) => (b.disabled = false));
    }
  };
  document.getElementById("allow")!.addEventListener("click", () => go("approve"));
  document.getElementById("deny")!.addEventListener("click", () => go("deny"));
}

interface Connection { id: string; name: string; connectedAt: string; lastRefreshed: string }

export async function connectorCard(el: HTMLElement) {
  const draw = (list: Connection[] | null, error = "") => {
    el.innerHTML = `
      <section class="card connector">
        <h3>Claude connector</h3>
        <p class="small">Chat with Claude in your Claude app (on your subscription), with access to Gold's data. Claude can read stocks, your portfolio and research, and save trades, research and notes here.</p>
        <div class="copy"><code id="mcp-url">${esc(connectorUrl())}</code><button class="ghost small" id="copy">Copy</button></div>
        <details${list && list.length ? "" : " open"}><summary class="small">How to connect</summary>
          <ol class="small">
            <li>In Claude (claude.ai or the app), open <b>Settings → Connectors → Add custom connector</b>.</li>
            <li>Name it <b>Gold</b> and paste the address above.</li>
            <li>Choose <b>Connect</b>, sign in here with your Gold account and tap <b>Allow</b>.</li>
            <li>In a chat, turn on Gold from the tools menu, then ask away.</li>
          </ol></details>
        <h4>Connected</h4>
        ${error ? `<div class="notice error">${esc(error)}</div>` : !list ? `<div class="loading"><span></span>Loading…</div>` : list.length
          ? `<ul class="plain conns">${list.map((c) => `<li><span><b>${esc(c.name)}</b><small class="muted"> since ${date(c.connectedAt)}</small></span><button class="ghost small" data-disconnect="${esc(c.id)}">Disconnect</button></li>`).join("")}</ul>`
          : `<p class="muted small">Nothing connected yet.</p>`}
      </section>`;
    el.querySelector("#copy")!.addEventListener("click", async (e) => {
      await navigator.clipboard?.writeText(connectorUrl()).catch(() => {});
      (e.target as HTMLButtonElement).textContent = "Copied";
    });
    el.querySelectorAll<HTMLButtonElement>("[data-disconnect]").forEach((b) => b.addEventListener("click", async () => {
      if (!confirm("Disconnect this app from Gold? It will need to connect again to use Gold.")) return;
      b.disabled = true;
      const r = await api<{ connections: Connection[] }>(`connections/${b.dataset.disconnect}`, "DELETE");
      draw(r.connections);
    }));
  };
  draw(null);
  try {
    draw((await api<{ connections: Connection[] }>("connections")).connections);
  } catch (e) {
    draw([], (e as Error).message);
  }
}
